import { I18n } from "@assets/strings";
import debounce from "lodash/debounce";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { Environment } from "@core/config/environment";
import { useServices } from "@core/servicesContext";
import { allCountrycodes, countries, Country, countryCodes } from "@domain/currentProfile/countryCode";
import { ExperienceType } from "@domain/experience/experience";
import { ExperienceDraft } from "@domain/experience/experienceDraft";
import { Organization } from "@domain/organization/organization";
import { useAsync } from "@utils/hooks";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { PrimaryBold } from "@ui/components/containers/primary";
import { CountryDatalist } from "@ui/components/inputs/countryDatalist";
import { DatePicker } from "@ui/components/inputs/datePicker";
import { useForm } from "@ui/components/inputs/hooks";
import { RadioButton } from "@ui/components/inputs/radio";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { stack } from "@ui/components/layout";
import { ProfilePicture } from "@ui/components/profile/profilePicture";
import { OrganizationSuggestions } from "./organizationSuggestions";

interface ExperienceEditorProps extends React.HTMLAttributes<HTMLFormElement> {
	initialExperience: ExperienceDraft;
	initialOrganization?: Organization;
	experienceId?: string;
	onSave: () => void;
}
export const ExperienceEditor: React.FC<ExperienceEditorProps> = ({
	initialExperience,
	experienceId,
	onSave,
	initialOrganization,
	...htmlProps
}) => {
	const { fieldProps, formValue, setValue } = useForm(
		{
			organizationName: initialExperience.organizationName ?? "",
			diploma: initialExperience.diploma ?? "",
			domain: initialExperience.domain ?? "",
			title: initialExperience.title,
		},
		Environment.APP_VARIANT === "jaangal"
			? ["organizationName", "diploma"]
			: ["organizationName", "diploma", "domain", "title"],
		{
			organizationName: (n) => n.length < 128,
		}
	);

	const isAcademic = initialExperience.type === ExperienceType.ACADEMIC;
	const currentYear = new Date().getFullYear();

	const { organizationName, title, diploma, domain } = formValue;
	const [isCurrent, setIsCurrent] = useState(!initialExperience.end);
	const [asTeacher, setAsTeacher] = useState(initialExperience.asTeacher);

	const [country, setCountry] = useState<Country | undefined>(
		initialExperience.countryCode ? countries[initialExperience.countryCode] : undefined
	);

	const [startDate, setStartDate] = useState<Date | undefined>(initialExperience.start);
	const [endDate, setEndDate] = useState<Date | undefined>(initialExperience.end);

	const [search, setSearch] = useState(organizationName);
	const [chosenOrganization, setChosenOrganization] = useState<Organization | null>(initialOrganization ?? null);

	const experience: ExperienceDraft = {
		title,
		countryCode: country && countryCodes[country],
		type: initialExperience.type,
		diploma,
		domain,
		start: startDate ?? new Date(),
		end: endDate,
		asTeacher,
		...(chosenOrganization ? { organizationId: chosenOrganization.id } : { organizationName }),
	};

	const { experienceService, trackingService } = useServices();
	const { loading, run } = useAsync(
		() => experienceService.saveExperience(experience, experienceId),
		[experience, experienceId]
	);

	const debouncedSearch = useCallback(
		debounce((v) => v.length >= 3 && setSearch(v), 500, { leading: true, trailing: true }),
		[]
	);

	function isValid(): boolean {
		if (Environment.APP_VARIANT === "jaangal") {
			return !!(experience.organizationName || experience.organizationId) && !!experience.start && !!experience.diploma;
		}
		const isExperienceValid =
			!!(experience.organizationName || experience.organizationId) && !!experience.start && !!experience.title;
		if (experience.type === "PROFESSIONAL") return isExperienceValid;
		return isExperienceValid && !!experience.domain && !!experience.diploma;
	}

	useEffect(() => {
		if (isCurrent) {
			setEndDate(undefined);
		}
	}, [isCurrent]);

	useEffect(() => {
		if (!chosenOrganization) {
			debouncedSearch(organizationName);
		} else if (organizationName !== chosenOrganization?.primaryName) {
			setChosenOrganization(null);
		}
	}, [organizationName]);

	useEffect(() => {
		if (chosenOrganization) {
			setValue({ ...formValue, organizationName: chosenOrganization.primaryName });
		}
	}, [chosenOrganization]);

	return (
		<Container
			{...htmlProps}
			onSubmit={async (e) => {
				e.preventDefault();
				const savedExperience = await run();
				if (!experienceId) {
					trackingService.track("experience / use organization", {
						organizationId: experience.organizationId,
						organizationName: savedExperience.organization.primaryName,
					});
				}
				createToast(I18n.t("success"), I18n.t("experience.save.success"), "success");
				onSave();
			}}
		>
			<InfoEdition>
				<PrimaryBold>{I18n.t(`experience.${initialExperience.type.toLowerCase()}.name.title`)}</PrimaryBold>
				<SimpleTextInput
					{...fieldProps("organizationName")}
					placeholder={I18n.t(`experience.${initialExperience.type.toLowerCase()}.name.placeholder`)}
					renderBefore={
						chosenOrganization && chosenOrganization.avatar
							? () => <StyledLogo picture={chosenOrganization.avatar} fallbackType={chosenOrganization.type} />
							: undefined
					}
					renderSuggestions={
						search.length >= 3 && !chosenOrganization
							? () => (
									<OrganizationSuggestions
										search={search}
										onChosen={(organization) => setChosenOrganization(organization)}
										onClose={(suggestions) => {
											const match = suggestions.find(
												(s) => s.primaryName.toLowerCase() === organizationName.toLowerCase()
											);
											if (match) {
												setChosenOrganization(match);
											}
										}}
									/>
								)
							: undefined
					}
				/>
			</InfoEdition>
			<InfoEdition>
				<PrimaryBold>{I18n.t("experience.country.title")}</PrimaryBold>
				<CountryDatalist
					id="countries"
					value={country}
					onChange={(v: string) => setCountry(v as Country)}
					placeholder={I18n.t("onboarding.country.placeholder")}
					options={allCountrycodes}
				/>
			</InfoEdition>
			{isAcademic && (
				<>
					<InfoEdition>
						<PrimaryBold>{I18n.t("experience.academic.diploma.title")}</PrimaryBold>
						<SimpleTextInput
							{...fieldProps("diploma")}
							placeholder={I18n.t("experience.academic.diploma.placeholder")}
						/>
					</InfoEdition>
					<InfoEdition>
						<PrimaryBold>{I18n.t("experience.academic.domain.title")}</PrimaryBold>
						<SimpleTextInput {...fieldProps("domain")} placeholder={I18n.t("experience.academic.domain.placeholder")} />
					</InfoEdition>
				</>
			)}
			<InfoEdition>
				<PrimaryBold>{I18n.t(`experience.${initialExperience.type.toLowerCase()}.title.title`)}</PrimaryBold>
				<SimpleTextInput
					{...fieldProps("title")}
					placeholder={I18n.t(`experience.${initialExperience.type.toLowerCase()}.title.placeholder`)}
				/>
			</InfoEdition>
			<InfoEdition>
				<PrimaryBold>{I18n.t("experience.start.label")}</PrimaryBold>
				<DatePicker
					value={startDate}
					onChange={(date) => setStartDate(date)}
					minYear={currentYear - 40}
					maxYear={endDate?.getFullYear()}
				/>
			</InfoEdition>
			{!isCurrent && (
				<InfoEdition>
					<PrimaryBold>{I18n.t("experience.end.label")}</PrimaryBold>
					<DatePicker
						value={endDate}
						onChange={(date) => setEndDate(date)}
						minYear={startDate ? startDate.getFullYear() : currentYear - 40}
					/>
				</InfoEdition>
			)}
			<RadioButton
				checkbox
				id="current"
				name="current"
				label={I18n.t(`experience.${initialExperience.type.toLowerCase()}.current.check`)}
				checked={isCurrent}
				onChange={() => setIsCurrent((c) => !c)}
			/>
			{!isAcademic && (
				<RadioButton
					checkbox
					id="teacher"
					name="teacher"
					label={I18n.t("experience.teacher.check")}
					checked={asTeacher ?? false}
					onChange={() => setAsTeacher((c) => !c)}
				/>
			)}
			<LoadingButton loading={loading} type="submit" disabled={!isValid()}>
				{I18n.t(experienceId ? "validate" : "create")}
			</LoadingButton>
		</Container>
	);
};

const Container = styled.form`
	${gap(30)};

	> button {
		margin: 0 auto;
	}
`;

const InfoEdition = styled.div`
	${stack(10, "flex-start", "stretch")};
`;

const StyledLogo = styled(ProfilePicture)`
	margin-left: 10px;
`;
